import type { PublicDocId } from '@/lib/api';

// Defines the feedback vocabulary: the scenes, targets, tags, and breadcrumb-event kinds
// feedback can express, plus the shapes that carry them. Field names are snake_case because
// a FeedbackSubmission is serialized to JSON when it is sent to the backend.

/** Enumerates the scene identifiers shared across the feedback ontology. */
export const FEEDBACK_SCENE_KIND = {
  DOC_THREAD: 'doc_thread',
  HOME_FEED: 'home_feed',
  PRODUCT_PAGE: 'product_page',
  GUIDE_PAGE: 'guide_page',
  UNKNOWN: 'unknown',
} as const;

/** Describes the allowed scene identifiers. */
export type FeedbackSceneKind = (typeof FEEDBACK_SCENE_KIND)[keyof typeof FEEDBACK_SCENE_KIND];

/** Enumerates the home-feed endpoint identifiers used by feedback scenes. */
export const FEEDBACK_HOME_FEED_ENDPOINT = {
  PUBLIC: 'public',
  UNIFIED: 'unified',
} as const;

/** Describes the allowed home-feed endpoint identifiers. */
export type FeedbackHomeFeedEndpoint =
  (typeof FEEDBACK_HOME_FEED_ENDPOINT)[keyof typeof FEEDBACK_HOME_FEED_ENDPOINT];

/** Enumerates the target identifiers shared across the feedback ontology. */
export const FEEDBACK_TARGET_KIND = {
  DOC: 'doc',
  FEED_ITEM: 'feed_item',
  PRODUCT: 'product',
  TOPIC: 'topic',
} as const;

/** Describes the allowed target identifiers. */
export type FeedbackTargetKind = (typeof FEEDBACK_TARGET_KIND)[keyof typeof FEEDBACK_TARGET_KIND];

/** Enumerates the breadcrumb event identifiers shared across the feedback ontology. */
export const FEEDBACK_BREADCRUMB_KIND = {
  NAVIGATION: 'navigation',
  ACTION: 'action',
  NOTIFICATION: 'notification',
  ERROR: 'error',
} as const;

/** Describes the allowed breadcrumb event identifiers. */
export type FeedbackBreadcrumbKind =
  (typeof FEEDBACK_BREADCRUMB_KIND)[keyof typeof FEEDBACK_BREADCRUMB_KIND];

/** Describes the product surface the user was on when they opened feedback. */
export type Scene =
  | {
      /** Identifies the scene as a document thread view. */
      kind: typeof FEEDBACK_SCENE_KIND.DOC_THREAD;
      /** Stores the public thread identifier shown in the doc route. */
      thread_id: PublicDocId;
      /** Stores the resolved main topic when known for the thread. */
      main_topic: string | null;
    }
  | {
      /** Identifies the scene as the home feed. */
      kind: typeof FEEDBACK_SCENE_KIND.HOME_FEED;
      /** Distinguishes the anonymous and authenticated home feed variants. */
      endpoint: FeedbackHomeFeedEndpoint;
    }
  | {
      /** Identifies the scene as a product page. */
      kind: typeof FEEDBACK_SCENE_KIND.PRODUCT_PAGE;
      /** Stores the product reference derived from the route. */
      product_ref: string;
    }
  | {
      /** Identifies the scene as a guide page. */
      kind: typeof FEEDBACK_SCENE_KIND.GUIDE_PAGE;
      /** Stores the guide topic key derived from the route. */
      topic_key: string;
    }
  | {
      /** Identifies scenes that do not match a tracked surface. */
      kind: typeof FEEDBACK_SCENE_KIND.UNKNOWN;
    };

/** Describes the specific entity the user targeted when opening feedback. */
export type Target =
  | {
      /** Identifies the target as a document thread. */
      kind: typeof FEEDBACK_TARGET_KIND.DOC;
      /** Stores the targeted document thread identifier. */
      id: PublicDocId;
    }
  | {
      /** Identifies the target as a feed item. */
      kind: typeof FEEDBACK_TARGET_KIND.FEED_ITEM;
      /** Stores the targeted feed item identifier. */
      id: PublicDocId;
    }
  | {
      /** Identifies the target as a product page. */
      kind: typeof FEEDBACK_TARGET_KIND.PRODUCT;
      /** Stores the targeted product reference. */
      product_ref: string;
    }
  | {
      /** Identifies the target as a guide topic. */
      kind: typeof FEEDBACK_TARGET_KIND.TOPIC;
      /** Stores the targeted guide topic key. */
      topic_key: string;
    }
  | null;

/** Records a breadcrumb event captured before the user submitted feedback. */
export type Breadcrumb = {
  /** Stores the event timestamp in milliseconds since epoch. */
  ts: number;
  /** Identifies the breadcrumb category used by feedback consumers. */
  kind: FeedbackBreadcrumbKind;
  /** Stores the short label shown for the breadcrumb event. */
  label: string;
  /** Stores optional structured details for richer breadcrumb events. */
  detail?: Record<string, unknown>;
};

/** Defines the full client-side feedback payload submitted by the form. */
export type FeedbackSubmission = {
  /** Stores the tags the user explicitly selected. */
  tags: string[];
  /** Stores offered tags the user left unselected. */
  unselected_tags: string[];
  /** Stores the freeform message entered by the user. */
  message: string;
  /** Stores the scene captured when feedback was opened. */
  scene: Scene;
  /** Stores the optional entity the user targeted when opening feedback. */
  target: Target;
  /** Stores the breadcrumb trail captured before submission. */
  breadcrumbs: Breadcrumb[];
  /** Stores the sanitized route captured from the browser location. */
  route: {
    /** Stores the browser pathname at capture time. */
    pathname: string;
    /** Stores the filtered query string preserved for feedback capture. */
    search: string;
  };
  /** Stores browser environment fields attached to the submission payload. */
  env: {
    /** Stores the current browser user agent string. */
    user_agent: string;
    /** Stores the viewport width at capture time. */
    viewport_w: number;
    /** Stores the viewport height at capture time. */
    viewport_h: number;
    /** Stores the active theme mode seen by the user. */
    color_scheme: 'light' | 'dark';
  };
};

/** Fails exhaustive unions when a caller reaches an unsupported variant. */
export function assertNever(x: never): never {
  throw new Error('Unexpected value: ' + JSON.stringify(x));
}

/** Builds a document-thread scene for the given public thread identifier. */
export function sceneDocThread(thread_id: PublicDocId, main_topic: string | null): Scene {
  return { kind: FEEDBACK_SCENE_KIND.DOC_THREAD, thread_id, main_topic };
}

/** Builds a home-feed scene for the current authenticated or public endpoint. */
export function sceneHomeFeed(endpoint: FeedbackHomeFeedEndpoint): Scene {
  return { kind: FEEDBACK_SCENE_KIND.HOME_FEED, endpoint };
}

/** Builds a product-page scene for the given product reference. */
export function sceneProductPage(product_ref: string): Scene {
  return { kind: FEEDBACK_SCENE_KIND.PRODUCT_PAGE, product_ref };
}

/** Builds a guide-page scene for the given topic key. */
export function sceneGuidePage(topic_key: string): Scene {
  return { kind: FEEDBACK_SCENE_KIND.GUIDE_PAGE, topic_key };
}

/** Builds the fallback scene used when no tracked route matches. */
export function sceneUnknown(): Scene {
  return { kind: FEEDBACK_SCENE_KIND.UNKNOWN };
}

/** Builds a document-thread target for feedback opened from a doc surface. */
export function targetDoc(id: PublicDocId): Target {
  return { kind: FEEDBACK_TARGET_KIND.DOC, id };
}

/** Builds a feed-item target for feedback opened from a feed surface. */
export function targetFeedItem(id: PublicDocId): Target {
  return { kind: FEEDBACK_TARGET_KIND.FEED_ITEM, id };
}

/** Builds a product target for feedback opened from a product surface. */
export function targetProduct(product_ref: string): Target {
  return { kind: FEEDBACK_TARGET_KIND.PRODUCT, product_ref };
}

/** Builds a topic target for feedback opened from a guide surface. */
export function targetTopic(topic_key: string): Target {
  return { kind: FEEDBACK_TARGET_KIND.TOPIC, topic_key };
}
